// @flow
import React, { Component } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';

import BackgroundImage from 'containers/fragments/BackgroundImageContainer';
import Breadcrumb from 'components/fragments/Breadcrumb';
import Loading from 'components/fragments/Loading';
import SubMenuHeader from 'components/header/SubMenuHeader';
import RencontreHeaderStructure from 'components/rencontre/header/RencontreHeaderStructure';
import RencontreHeaderStatus from 'components/rencontre/header/RencontreHeaderStatus';
import RencontreHeaderFooter from 'components/rencontre/header/RencontreHeaderFooter';

import { STATUS_SUCCESS, STATUS_FAILURE } from 'constants/statusConstants';
import { formatFullDate } from 'utils/dateUtils';
import { dynamicClassName } from 'utils/dynamicClassName';
import { getMatchScore, getLastMinute } from 'utils/rencontreUtils';
import { getMetaBySlug } from 'utils/seoUtils';

import type { RouterProps } from 'types/Router';
import type { Status } from 'types/Status';
import type { RencontreType } from 'types/Rencontre';
import type { CompetitionEquipeType } from 'types/Settings';
import type { AlbumType } from 'types/Albums';

export type StateProps = {
  status: Status,
  competitionsEquipes: CompetitionEquipeType,
  clubName: string,
  rencontre: RencontreType,
  album: AlbumType,
  albumStatus: Status
};

type Props = RouterProps & StateProps;

type State = {
  currentTeam: ?{
    equipe_nom: string,
    equipe_slug: string
  }
};

class RencontreHeader extends Component<Props, State> {
  state: State = {
    currentTeam: null
  };

  shouldComponentUpdate() {
    return true;
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const {
      competitionsEquipes,
      rencontre: {
        competition: { id }
      },
      status
    } = nextProps;

    if (status === STATUS_SUCCESS && competitionsEquipes && id && prevState.currentTeam === null) {
      const hasCompetition = id && Object.prototype.hasOwnProperty.call(competitionsEquipes, id);
      const currentTeam = hasCompetition ? competitionsEquipes[id] : null;

      return { currentTeam };
    }

    return null;
  }

  getSubMenus = () => {
    const {
      rencontre: { etat, actions, localJoueurs },
      album
    } = this.props;

    const subMenus = [];

    if (etat === 'Planifiée' || etat === 'Annulée') {
      subMenus.push({
        menu_order: 5,
        object_slug: 'general',
        title: 'Avant-match',
        description: '',
        featured_media: { src: '' },
        texte_cta: '',
        object_id: 0,
        type_name: 'endur',
        types_public: []
      });
    }

    if (etat === 'Terminée' || etat === 'Score validé' || etat === 'Présaisie') {
      const noAction = actions.every(action => action.localActions.length === 0 && action.visiteurActions.length === 0);

      if (noAction && (!localJoueurs || localJoueurs.length === 0)) {
        subMenus.push({
          menu_order: 10,
          object_slug: 'statistiques-classement',
          title: 'Statistiques & Classement',
          description: '',
          featured_media: { src: '' },
          texte_cta: '',
          object_id: 1,
          type_name: 'endur',
          types_public: []
        });
      } else {
        subMenus.push(
          {
            menu_order: 5,
            object_slug: 'general',
            title: 'Résumé',
            description: '',
            featured_media: { src: '' },
            texte_cta: '',
            object_id: 0,
            type_name: 'endur',
            types_public: []
          },
          {
            menu_order: 10,
            object_slug: 'statistiques-classement',
            title: 'Statistiques & Classement',
            description: '',
            featured_media: { src: '' },
            texte_cta: '',
            object_id: 1,
            type_name: 'endur',
            types_public: []
          }
        );
      }
      if (album && album.id) {
        subMenus.push({
          menu_order: 15,
          object_slug: 'medias',
          title: 'Photos & Vidéos',
          description: '',
          featured_media: { src: '' },
          texte_cta: '',
          object_id: 2,
          type_name: 'endur',
          types_public: []
        });
      }
    }

    if (etat === 'En direct') {
      subMenus.push({
        menu_order: 5,
        object_slug: 'general',
        title: 'Match en direct',
        description: '',
        featured_media: { src: '' },
        texte_cta: '',
        object_id: 0,
        type_name: 'endur',
        types_public: []
      });
    }

    return subMenus;
  };

  _renderFooter = () => {
    const { currentTeam } = this.state;
    const {
      rencontre: {
        terrain,
        etat,
        localStructure,
        visiteurStructure,
        dateEffective,
        dateEffectiveStr,
        competition: { nom },
        arbitres
      },
      clubName,
      competitionsEquipes
    } = this.props;

    if (Object.entries(competitionsEquipes).length === 0) {
      return <Loading />;
    }

    const separator = currentTeam && currentTeam.equipe_nom !== '' ? ' : ' : '';
    const equipeNom = (currentTeam && currentTeam.equipe_nom) || '';

    const title = `${equipeNom.replace('&#8211;', '-')}${separator}${nom} - ${localStructure.nom} : ${
      visiteurStructure.nom
    }`;

    const arbitre = (arbitres.length > 0 && `${arbitres[0].prenom} ${arbitres[0].nom}`) || '';

    return (
      <>
        <RencontreHeaderFooter
          etat={etat}
          dateEffective={dateEffective}
          dateEffectiveStr={dateEffectiveStr}
          title={title}
          adress={terrain.address}
          club={clubName}
        />
        <div className="banner__infos container">
          <div>
            <span className="ft-tertiary ft-up">
              {`${terrain.nom} ${terrain.localite ? `- ${terrain.localite}` : ''}`}
            </span>
            <span className="ft-tertiary ft-up mb-0">{arbitre !== '' ? `Arbitre : ${arbitre}` : ''}</span>
          </div>
        </div>
      </>
    );
  };

  render() {
    const { currentTeam } = this.state;
    const {
      rencontre: {
        id,
        competition: { nom: competitionNom },
        journee: { nom: journeeNom },
        localStructure,
        visiteurStructure,
        dateEffective,
        etat,
        eligibleFDMD,
        resultVisiteur: { pointsDeMarque: scoreVisiteur },
        resultLocal: { pointsDeMarque: scoreLocal },
        actions,
        forfait: matchForfait
      },
      status,
      match: {
        params: { slug }
      }
    } = this.props;

    // console.log('forfait', forfait);

    if (status === STATUS_FAILURE) {
      return <Redirect to="/404" />;
    }

    if (status !== STATUS_SUCCESS) {
      return <Loading />;
    }

    const { scoreVis, scoreLoc } = getMatchScore(actions);
    const equipeSlug = (currentTeam && currentTeam.equipe_slug) || `/equipes/${(slug && slug) || ''}/`;

    const sectionClassname = dynamicClassName('banner banner--match');
    etat && etat === 'Score validé' && sectionClassname.add('banner--match-ended');
    etat && etat === 'Planifiée' && sectionClassname.add('banner--wButton');

    const visitScore = scoreVis < scoreVisiteur ? scoreVisiteur : scoreVis;
    const localScore = scoreLoc < scoreLocal ? scoreLocal : scoreLoc;
    const lastMinute = getLastMinute(actions);

    return (
      <>
        <section className={sectionClassname.build()}>
          <BackgroundImage
            className="banner__thumbnail img-blue-dark"
            illustration={'/img/banner/match.jpg'}
            baliseImg={false}
          />
          <div className="container">
            <Breadcrumb
              elements={[
                { name: 'accueil', to: '/' },
                { name: 'Équipes', to: '/equipes' },
                { name: (currentTeam && currentTeam.equipe_nom.replace('&#8211;', '-')) || '', to: equipeSlug }
              ]}
              isDark
            />
            <h1 className="ft-center ft-up">{competitionNom}</h1>
            <h2 className="ft-center ft-up">{`${journeeNom} — ${formatFullDate(dateEffective)}`}</h2>
            <div className="banner__scoreboard">
              <RencontreHeaderStructure {...localStructure} />
              <RencontreHeaderStatus
                forfait={matchForfait}
                etat={etat}
                id={id}
                direct={lastMinute}
                dateEffective={dateEffective}
                scoreLocal={String(localScore) || '0'}
                scoreVisiteur={String(visitScore) || '0'}
                eligibleFDMD={eligibleFDMD}
              />
              <RencontreHeaderStructure {...visiteurStructure} />
            </div>
            {this._renderFooter()}
          </div>
        </section>
        <SubMenuHeader
          subMenus={this.getSubMenus()}
          subMenuPath={`${equipeSlug}${equipeSlug[equipeSlug.length - 1] === '/' ? '' : '/'}match/${id}`}
          status={STATUS_SUCCESS}
          notHidden
          navClass="box"
          niveauMenu={5}
        />
      </>
    );
  }
}

export default withHelmet(withRouter(RencontreHeader))((Helmet, pageProps) => {
  const { clubName, rencontre } = pageProps;

  const dateRencontre = rencontre.dateEffective.getTime();
  let now = new Date();
  now = now.getTime();
  let slug = '';
  if (dateRencontre > now) {
    slug = 'match-prochain';
  } else if (dateRencontre < now) {
    slug = 'match-resume';
  }
  const meta = getMetaBySlug(slug);

  return (
    <Helmet>
      <title>{meta.title(clubName)}</title>
      <meta name={'description'} content={meta.description()} />
      <meta name={'keywords'} content={meta.keywords(clubName)} />
    </Helmet>
  );
});
